import {
  action,
  computed,
  observable,
  toJS,
  autorun,
  when,
  reaction,
} from 'mobx';
import Alert from 'react-s-alert';
import i18next from 'i18next';
import { throttle } from 'throttle-debounce';

import { AppManager, Input, Variables } from 'core';
import { routerStore } from 'core/routing';
import { analytics } from 'core/analytics';
import FileAppManager from 'apps/file/appManager';
import { ConversionProgressSubscription } from 'apps/file/gql';

import { FormStore } from 'libs/forms/app';
import {
  CreateRFQMutation,
  RFQQuery,
  AddQuotingPriorityMutation,
  AssignRFQToManufacturerMutation,
  UpdateRFQMutation,
  FileRequirementsQuery,
  UpdateRequestForQuoteStateMutation,
} from 'apps/rfq/gql';
import {
  JobSpecModel,
  RFQModel,
  RFQApp,
  JobSpecApp,
  // MaterialApp,
  // ProcessApp,
  RFQAssigneesModel,
  OptionValueModel,
} from 'apps/rfq/app';

import { conversionsStore } from 'apps/conversions/appManager';
import { clientStore } from 'apps/client/app';
import { ProjectContextModel, ProjectContextApp } from 'apps/context/app';

import QualityForm from 'apps/rfq/rfqPage/views/qualityForm';
import { modalStore } from 'apps/modals/app';
import rfqConfig from 'apps/rfq/config';
import {
  fromLocalStorage,
  toLocalStorage,
  fromSessionStorage,
  toSessionStorage,
} from 'core/localStorage';
import SignUpModal from 'apps/profile/signUp/modal';

import { JobSpecAppManager } from 'apps/jobSpec/appManager';
import { ensureIsGraphqlId } from 'utils/dgraphId';

const assignRFQToManufacturerConfig = {
  rfqassigner: {
    fields: ['assignee'],
  },
  title: '',
  fields: {
    assignee: {
      type: 'manufacturer', // should be 'manufacturers'
      label: '',
    },
  },
};

const addressConfigForRFQPage = {
  addressForm: {
    fields: ['address'],
    styles: {
      formColumns: 1,
      labelBold: true,
    },
  },
  title: 'Order Details.',
  fields: {
    address: {
      // label: null,
      doNotRenderAddressValue: true,
      placeholder: 'Choose an Address',
      component: 'addresses.AddressChoiceField', // what to do if no addresses have been added - problem with adding an address and selecting it at the same time
      noValidation: true,
      oneColumn: true,
      preventAutoSelectionOfDefaultAddress: true,
    },
  },
};

const addressConfigForRFQForm = {
  addressForm: {
    fields: ['address'],
    styles: {
      formColumns: 1,
      labelBold: true,
    },
  },
  title: 'Order Details.',
  fields: {
    address: {
      label: 'Shipping Address',
      placeholder: 'Choose an Address',
      component: 'addresses.AddressChoiceField', // what to do if no addresses have been added - problem with adding an address and selecting it at the same time
      noValidation: true,
      oneColumn: true,
      optional: true,
    },
  },
};

class QualityFormData extends Variables {
  @observable howWouldYouPreferEngagingWithUs = '';
  @observable openToReceiveDesignFeedback = '';
  @observable helpChossingTech = '';
  @observable helpChossingMaterial = '';
  @observable moreAboutPrototype = '';
  @observable whenOrder = '';
  @observable oneTimeOrAdditional = '';
  @observable preferredDelivery = '';
  @observable additionalInfo = '';
  @observable desiredPrize = '';
  @observable locationPreference = '';
  @observable howToGetQuote = '';
  @observable sustainableApproach = '';

  // @observable sampleUnit = ''; // Old property. Removed in V2
}

export default class RFQAppManager extends AppManager {
  static registryName = 'RFQAppManager';
  fileId = null;
  file = null;
  spaceId = null;
  rfqId = null;
  options = [];

  @observable assignRFQToManufacturerFormStore = null;
  @observable assignedManufacturerTarget = null;
  @observable conversionProgress = '';
  @observable conversionSteps = '';
  @observable shippingAddressFormTarget = {};
  @observable addressFormStore = {};
  @observable updateAddressFormStore = {};

  @observable addressRefs = {}; // for rfq page

  formLoaded = false;
  hasQualityFormBeenShown = false;
  isFirstRfqLoad = true;

  constructor(
    parent,
    { file = null, signedInProfile = null, rfqId = null, viewerControl = null },
    options,
  ) {
    super(parent, null, options);

    this.viewerControl = viewerControl;

    this.file = file;
    this.fileId = file?.id;
    // When we want people to do RFQs from Orgs, spaceId should be determined by the organisation or profile chosen (or role mode).
    this.spaceId = signedInProfile?.inSpace.id;

    this.rfqId = ensureIsGraphqlId(rfqId, 'RFQ');

    this.AppRef('rfqApp', RFQApp);
    this.AppRef('contextApp', ProjectContextApp);

    this.qualityFormData = new QualityFormData();
    this.qualityFormData.onChange(
      Object.keys(this.qualityFormData),
      this.OnFormDataChange,
    );

    this.InitRFQAssignerElements();

    if (!this.rfqId) {
      // why branch here when all the same attributes could in theory be used in an 'edit' context
      this.rfqApp = new RFQApp(this);
      this.rfqApp.jobSpec = new JobSpecApp(this.rfqApp, {
        // rfq: this.rfqApp,
      });

      this.jobSpecManager = new JobSpecAppManager(
        this,
        { jobSpecApp: this.rfqApp.jobSpec },
        { autoDispose: false },
      );

      this.LoadRFQFormFromStorage();

      // if we just came back to this url, try to autosubmit this form created as anonymous user
      if (
        sessionStorage.getItem('pendingRFQSubmission') ===
        routerStore.route.params.sharedFileId
      ) {
        // need to check for file because there is potentially an initial construction
        // with file === null
        if (
          this.jobSpecManager.isValid &&
          clientStore.signedInProfile &&
          this.file
        ) {
          this.SubmitRFQ();
        }
      }

      this.addressFormStore = new FormStore(
        this.rfqApp,
        addressConfigForRFQForm,
        'addressForm',
      );

      this.initFileRequirementsReaction();
      this.initRFQFormSerializationReaction();
    } else {
      // attach quotes queryManager here or create using useAppManager?
      this.variables = new Variables({
        id: () => this.rfqId,
      });
      this.variables.onChange(['id'], this.LoadRfqQuery);
    }
    /*
    TODO: re-initialise queries on switching Portals.

    this.disposers.push(
      observe(clientStore, 'currentPortal', () => this.initiateQuery(), true),
      );
      */
  }

  @computed get relevantSpaceId() {
    if (this.file?.private) {
      return this.file.space.parent.id;
    }

    return clientStore.activeSpaceId;
  }

  initFileRequirementsReaction = () => {
    const disposer = autorun(() => {
      const { build } = this.rfqApp?.jobSpec;
      if (!build) return;
      const supportingFileIds = build.supportingFiles?.map(
        supportingFile => supportingFile.id,
      );

      const optionValues = this.rfqApp.jobSpec?.build.valuesTarget;

      const buildValueInputs = Object.values(optionValues)
        .filter(
          optVal => !(optVal === null || optVal.option.name === 'Lead Time'),
        )
        .map(optVal => {
          const buildVal = {
            optionId: optVal.option.id,
          };
          buildVal[optVal.option.valueKey] = optVal.value;
          return buildVal;
        });

      const input = {
        fileId: this.fileId,
        materialId: build.material?.id,
        processId: build.process?.id,
        buildValueInputs,
        supportingFileIds,
      };
      if (!input.fileId || !input.materialId || !input.processId) {
        this.rfqApp.fileRequirements = [];
        this.rfqApp.areFileRequirementsValidatedByServer = false;
        return;
      }
      this.QueryFileRequirements(input);
    });

    this.disposers.push(disposer);
  };

  initRFQFormSerializationReaction() {
    // Only serialize for signed out users
    if (clientStore.signedInProfile) return;

    const disposer = reaction(
      () => ({
        process: this.rfqApp.jobSpec.build.process,
        material: this.rfqApp.jobSpec.build.material,
        quantity: this.rfqApp.jobSpec.quantity,
        values: Object.values(this.rfqApp.jobSpec.build.valuesTarget),
      }),
      throttle(1000, this.saveRFQFormToStorage),
    );

    this.disposers.push(disposer);
  }

  @action LoadRfqQuery = () => {
    const q = new RFQQuery(this, {
      queryKey: 'rfq',
      // onSysErr: this.onRfqQueryError,
    });
    q.process(rfq => RFQModel.Load(rfq), this.RfqUpdate);
    q.process(
      context => (context ? ProjectContextModel.Load(context) : null),
      this.UpdateContext,
      { subPath: 'intermediatorSession.shareFile.context' },
    );
    q.query();
  };

  @action UpdateContext = contextModel => {
    if (!contextModel) return;
    this.contextApp = contextModel;

    if (!this.hasQualityFormBeenShown) {
      this.hasQualityFormBeenShown = true;

      this.loadQualityFormDataFromServer();

      if (
        !fromLocalStorage(this.skipQualityFormStorageKey) &&
        !this.allQuestionsAnswered &&
        !clientStore.hasRole('INTERMEDIATOR')
      ) {
        setTimeout(() => {
          if (this.rfqApp?.state !== 'INCOMPLETE') {
            this.showQualityForm();
          }
        }, 2000);
      }
    }
  };

  @computed get loading() {
    return this.queries.get('rfq')?.loading;
  }

  @computed get skipQualityFormStorageKey() {
    return `rfq-${this.rfqId}-skip`;
  }

  @computed get submittingRfqForm() {
    return this.jobSpecManager.submitting;
  }

  @action loadQualityFormDataFromServer = () => {
    // Update the variables using the server data
    if (this.rfqApp?.quotingPriorityData?.length > 0) {
      const answersFromServer = JSON.parse(this.rfqApp.quotingPriorityData);

      Object.keys(answersFromServer).forEach(k => {
        this.qualityFormData[k] = answersFromServer[k];
      });
    }
  };

  @computed get allQuestionsAnswered() {
    if (this.rfqApp.quotingPriorityData === undefined) {
      return false;
    }
    const keys = Object.keys(this.qualityFormData);
    const answeredQuestions = JSON.parse(this.rfqApp.quotingPriorityData);

    if (answeredQuestions === null) return false;
    if (keys.length !== Object.keys(answeredQuestions).length) return false;

    if (
      Object.keys(answeredQuestions).some(
        key => answeredQuestions[key].length === 0,
      )
    )
      return false;

    return true;
  }

  @computed get someQuestionsAnswered() {
    if (this.allQuestionsAnswered) return true;
    if (this.rfqApp.quotingPriorityData === undefined) {
      return false;
    }
    const answeredQuestions = JSON.parse(this.rfqApp.quotingPriorityData);
    if (answeredQuestions === null) return false;

    delete answeredQuestions.disposers;
    delete answeredQuestions.props;

    if (
      Object.keys(answeredQuestions).some(
        key => answeredQuestions[key].length > 0,
      )
    )
      return true;
    return false;
  }

  @action showQualityForm = () => {
    const config = { ...rfqConfig };
    // Create the required formstore
    this.qualityform = new FormStore(
      this.qualityFormData,
      config,
      'qualityform',
    );

    // Even if we set the data inside the mobx object, RadioGroup fields
    // still requires to know the checkedOptionIndex.
    // Here, we update the config object to start with the right values
    // TODO: Move this to the RadioGroupField element
    Object.keys(config.fields).forEach(k => {
      const field = config.fields[k];
      if (field.component === 'forms.RadioGroupField') {
        const { options } = field;
        const realValue = this.qualityFormData[k];
        for (let i = 0; i < options.values.length; i += 1) {
          const opt = options.values[i];
          if (opt.value === realValue) {
            field.checkedOptionIndex = i;
            break;
          }
        }
      }
    });

    // Show the qualification form as a modal
    modalStore.SetModalComponent(
      QualityForm,
      {
        formStore: this.qualityform,
        buttonHandler: this.confirmQualificationForm,
        skipAction: this.skipAction,
        rfqApp: this.rfqApp,
      },
      () => {
        // Disable automatic form
        toLocalStorage(
          this.skipQualityFormStorageKey,
          { clickedSkip: true },
          true,
        );
      },
    );
  };

  skipAction = () => {
    analytics.sendEvent(
      'rfq-qualification-form-skip',
      {},
      {
        ga: true,
      },
    );
    this.CloseQualificationForm();
  };

  @computed get viewerFile() {
    const { signedInProfile } = clientStore;
    const { firstFile, revision } = this.rfqApp.intermediatorSession.shareFile;
    const { body } = this.rfqApp.jobSpec.build;
    let file = null;
    let partId = null;
    if (revision && revision.file) {
      // - this is the standard case, every RFQ is a shared file, and every shared
      // file is a versioned repository, we use the head revision of the shared file
      // as the file that we want to pass to the viewer

      // eslint-disable-next-line prefer-destructuring
      file = revision.file;
    } else if (
      signedInProfile?.user &&
      body &&
      (body.srcFile?.creator?.id === signedInProfile.user.id ||
        body.srcFile?.creator?.username === '@tmpuser')
    ) {
      // - when there is no revision, and there is a body with a srcFile in the build,
      // then this rfq is for an extracted part, and if the creator of the srcFile is
      // the user that is currently logged in, then we can use the srcFile and partId
      // as fallback for the viewer
      // - in practice this means a user will extract a part, and while the part is not
      // extracted+converted yet, he will simply see the assembly with the part open in
      // a viewer tab instead
      // - when the creator is tmpuser then the user was not logged in when he uploaded
      // the assembly and then logged in, so treat this the same is if the user was the
      // creator
      const { cadId, srcFile } = body;
      partId = cadId;
      file = srcFile;
    } else {
      // - when we do not have a revision, and do not own the srcFile, then we are
      // probably an intermediator looking at a rfq that has been recently created,
      // or is not working correctly, either way we can use the firstFile as fallback,
      // if it has no conversion the rfqAppManager should have subscribed to the gltf
      // conversion subscription
      file = firstFile;
    }

    return {
      file,
      partId,
    };
  }

  @action RfqUpdate = rfqModel => {
    if (!rfqModel) return;
    this.rfqApp = rfqModel;
    const { shareFile } = this.rfqApp.intermediatorSession;
    const { firstFile, revision } = shareFile;

    if (revision && revision.file) {
      // - standard case when creating a new RFQ should be there is shared file that
      // has a revision, and if that revision has no gltf conversion we need to subscribe
      // here
      const { conversionGltfjson } = revision.file;
      if (!conversionGltfjson || conversionGltfjson.status === 'PENDING') {
        // - if the conversion is pending then we need to subscribe, if it is anything other
        // then that it is ok to leave this conditional without subscribing at all, then it
        // is either sucessful or failed, and both are valid end states
        this.SubscribeConversionProgress(shareFile, revision.file);
      }
    } else if (
      firstFile &&
      (!firstFile.conversionGltfjson ||
        firstFile.conversionGltfjson.status === 'PENDING')
    ) {
      // - if we extract a part, then there is no revision, we fallback to the firstFile instead
      // (which is just the file that was first uploaded to the shared file, it is an alternative
      // to using the (head) revision)
      // - we only subscribe if it is pending, same as above, if its successful or failed already
      // we don't need to do anything
      this.SubscribeConversionProgress(shareFile, firstFile);
    }

    if (
      !this.conversionSubscription &&
      (!firstFile?.conversionGltfjson ||
        firstFile.conversionGltfjson.status === 'PENDING')
    ) {
      // - when there is no file, no conversion, and no subscription, something must
      // be wrong, as a last attempt we popup an error and suggest reloading the page
      Alert.error(
        'Sorry, we could not update the viewer with the correct file. You can try to reload the page to fix this.',
      );
    }

    this.setupAddressFormUpdater();

    if (this.isFirstRfqLoad) {
      // For Incomplete RFQs, if they are only incomplete because we are missing the address
      // then show the adress modal
      if (
        this.rfqApp.state === 'INCOMPLETE' &&
        !this.rfqApp.address &&
        this.rfqApp.fileRequirements.length === 0 &&
        !clientStore.hasRole('INTERMEDIATOR')
      ) {
        // we need to timeout, not only for user experience, but also because we need
        // to wait a react render cycle for the address form to be construct based off
        // setupAddressFormUpdater() that we just called and would not be ready if this was
        // executed immediately synchronously.
        setTimeout(() => {
          const description =
            'Please provide a delivery address so that we can include shipping costs in your final quote.';

          this.BeginChangeAddress(description);
        }, 3000);
      }
    }
    this.isFirstRfqLoad = false;
  };

  @action setupAddressFormUpdater() {
    this.shippingAddressFormTarget.address = this.rfqApp.address;

    addressConfigForRFQPage.fields.address.addressSpaceId =
      this.rfqApp.customerSpace.id;

    this.updateAddressFormStore = new FormStore(
      this.shippingAddressFormTarget,
      addressConfigForRFQPage,
      'addressForm',
    );

    if (this.addressFormUpdaterDisposer) this.addressFormUpdaterDisposer();
    this.addressFormUpdaterDisposer = autorun(
      () => {
        if (
          this.shippingAddressFormTarget.address?.id !== this.rfqApp.address?.id
        ) {
          this.UpdateShippingAddress(this.shippingAddressFormTarget.address);
        }
      },
      {
        fireImmediately: false,
      },
    );
    this.disposers.push(this.addressFormUpdaterDisposer);
  }

  @action UpdateRFQState = newState => {
    const updateInput = new Input({
      input: {
        id: this.rfqApp.id,
        state: newState,
      },
    });

    const onUpdateRequestForQuoteStateError = () => {
      Alert.error('Sorry, there was an error changing the RFQ State.');
    };

    const updateRequestForQuoteStateMutation =
      new UpdateRequestForQuoteStateMutation(updateInput, null, {}, true);
    updateRequestForQuoteStateMutation.setCallbacks({
      onSysErr: onUpdateRequestForQuoteStateError,
      onUserErr: onUpdateRequestForQuoteStateError,
      // onSuccess:() => { },
    });
    updateRequestForQuoteStateMutation.mutate();
  };

  @action SubscribeConversionProgress(sharedFile, file) {
    // should be inside FilAppManager?
    if (this.conversionSubscription) {
      this.conversionSubscription.CompleteSubscription();
    }

    const variables = {
      subscribeFileProcessInput: {
        fileId: file.id,
        conversionType: 'gltf',
        contextId: sharedFile.context.id,
      },
    };

    this.conversionSubscription = new ConversionProgressSubscription(this, {
      variables,
    });

    this.conversionSubscription.process(this.handleConversionProgress);
    this.conversionSubscription.StartSubscription();
  }

  @action handleConversionProgress = result => {
    if (!result) {
      return;
    }

    if (this.rfqApp.intermediatorSession.revision) {
      // - I wrapped these into a check since I want this function to able to
      // handle conversions for shared files which have no revision yet, I assume
      // some other component relies on these values
      // - all other code should probably fallback to using the firstFile as well,
      // this which is what this code does and why it is neccessary for this
      // function to handle cases where this.rfqApp.intermediatorSession.revision
      // is null
      this.rfqApp.intermediatorSession.revision.file.conversionGltfjson.status =
        result.status;
      this.rfqApp.intermediatorSession.revision.file.conversionGltfjson.reason =
        result.reason;
      this.rfqApp.intermediatorSession.revision.file.conversionGltfjson.errorCode =
        result.errorCode;
    }

    if (result.progress !== '') {
      this.conversionProgress = result.progress;
    }
    if (result.steps !== '') {
      this.conversionSteps = result.steps;
    }

    if (
      result.status === 'SUCCESS' ||
      result.status === 'ERROR' ||
      result.status === 'PDF'
    ) {
      this.conversionSubscription?.CompleteSubscription();
      this.conversionSubscription = null;
      this.LoadRfqQuery();
    }
  };

  @action Submit = () => {
    if (this.jobSpecManager.isValid) {
      if (!clientStore.signedInProfile) {
        sessionStorage.setItem(
          'pendingRFQSubmission',
          routerStore.route.params.sharedFileId,
        );
        modalStore.SetModalComponent(SignUpModal, {}, () => {
          // modal closed
          if (clientStore.signedInProfile) {
            this.SubmitRFQ();
          }
        });
      } else {
        this.SubmitRFQ();
      }
    } else {
      this.jobSpecManager.alert();
    }
  };

  @action SubmitRFQ() {
    sessionStorage.removeItem('pendingRFQSubmission');
    this.jobSpecManager.SetPosting(true);

    const optionValues = this.rfqApp.jobSpec?.build.valuesTarget;
    let leadTime;
    Object.keys(optionValues).forEach(optKey => {
      const val = optionValues[optKey];
      if (val.option.name === 'Lead Time') {
        leadTime = val.value;
      }
    });
    const buildValueInputs = Object.values(optionValues)
      .filter(
        optVal => !(optVal === null || optVal.option.name === 'Lead Time'),
      )
      .map(optVal => {
        const buildVal = {
          optionId: optVal.option.id,
        };
        buildVal[optVal.option.valueKey] = optVal.value;
        return buildVal;
      });

    const partId = routerStore.params.get('partId');
    const supportingFileIds = this.rfqApp.jobSpec?.build.supportingFiles?.map(
      supportingFile => supportingFile.id,
    );

    const rfqInputOptions = {
      jobSpec: {
        build: {
          fileId: this.fileId,
          materialId: this.rfqApp.jobSpec?.build?.material?.id,
          processId: this.rfqApp.jobSpec?.build?.process?.id,
          buildValueInputs,
          supportingFileIds,
        },
        leadTime,
        quantity: this.rfqApp.jobSpec?.quantity,
      },
      addressId: this.rfqApp.address?.id,
    };

    if (!partId) {
      // If it is a standard rfq (i.e. no extraction functionality is required)
      const rfqInput = new Input({
        input: rfqInputOptions,
        spaceId: clientStore.activeSpaceId,
      });
      const rfqMutation = new CreateRFQMutation(rfqInput, null, {}, true);
      rfqMutation.setCallbacks({
        onSysErr: this.onRfqCreationErr,
        onUserErr: this.onRfqCreationErr,
        onSuccess: this.onRfqCreationSuccess,
      });
      rfqMutation.mutate();
    } else {
      const names = [];
      const rootItem = this.viewerControl?.cadFile?.assembly?.GetProduct(0);
      if (rootItem?.name) {
        names.push(rootItem.name);
      }
      const cadItem = this.viewerControl?.cadItemByPartId(partId);
      if (cadItem?.name) {
        names.push(cadItem.name);
      }
      let partFilename = '';
      if (names.length > 0) {
        const randomSlug = Math.random().toString(36).slice(-5);
        names.push(randomSlug);
        partFilename = names.join('-').replace(/[ ./\\]/g, '_');
      }
      conversionsStore.StartExtraction(
        this.fileId,
        partFilename,
        partId,
        clientStore.signedInProfile.user.id,
        rfqInputOptions,
      );
    }

    const eventContent = {
      leadTime,
      quantity: this.rfqApp.jobSpec?.quantity,
      material: this.rfqApp.jobSpec?.build?.material?.name,
      process: this.rfqApp.jobSpec?.build?.process?.name,
    };

    // analytics events
    analytics.sendEvent('make-part-submitted-rfq', eventContent, {
      ga: true,
    });
    analytics.sendEvent('RFQ-submitted', eventContent, {
      intercom: true,
    });
    analytics.sendEvent(
      'Lead',
      { ...eventContent, currency: 'EUR', value: 100 },
      {
        facebook: true,
      },
    );
  }

  @action onRfqCreationErr = () => {
    this.jobSpecManager.SetPosting(false);
    Alert.error(
      i18next.t('Hmm... We failed to submit your RFQ. Please try again.'),
    );
  };

  @action onRfqCreationSuccess = result => {
    this.jobSpecManager.SetPosting(false);
    Alert.success(i18next.t('RFQ submitted successfully.'));
    routerStore.router.navigate('rfq', {
      rfqId: result.id,
    });
  };

  @action confirmQualificationForm = (closeOnFinish = true) => {
    if (this.qualityform === undefined) {
      return;
    }
    if (!this.qualityform.isValid) {
      this.qualityform.alert();
    }

    const stringData = JSON.stringify(toJS(this.qualityFormData));
    this.quotingPriorityInput = new Input({
      requestForQuoteId: () => this.rfqApp.id,
      quotingPriorityData: stringData,
    });

    this.quotingPriorityMutation = new AddQuotingPriorityMutation(
      this.quotingPriorityInput,
    );

    this.quotingPriorityMutation.process(
      response => {
        this.rfqApp.quotingPriorityData =
          response.addQuotingPriority.quotingPriorityData;
      },
      () => {
        if (closeOnFinish) this.CloseQualificationForm();
      },
      {},
    );

    if (closeOnFinish) {
      analytics.sendEvent(
        'rfq-qualification-form-done',
        {},
        {
          ga: true,
          intercom: true,
        },
      );
    } else {
      analytics.sendEvent(
        'rfq-qualification-info-added',
        {},
        {
          ga: true,
          intercom: true,
        },
      );
    }

    this.quotingPriorityMutation.mutate();
  };

  @action CloseQualificationForm = () => {
    toLocalStorage(this.skipQualityFormStorageKey, { clickedSkip: true }, true);
    modalStore.Close();
  };

  @action OnFormDataChange = () => {
    this.confirmQualificationForm(false);
  };

  @action InitRFQAssignerElements = () => {
    this.assignedManufacturerTarget = new RFQAssigneesModel();

    this.assignRFQToManufacturerFormStore = new FormStore(
      this.assignedManufacturerTarget,
      assignRFQToManufacturerConfig,
      'rfqassigner',
      false, // disable auto-focus
    );

    this.assignRFQToManufacturerInput = new Input({
      requestForQuoteId: () => this.rfqApp.id,
      supplierIds: () => [this.assignedManufacturerTarget.assignee.id],
    });

    this.assignRFQToManufacturerMutation = new AssignRFQToManufacturerMutation(
      this.assignRFQToManufacturerInput,
    );

    this.assignRFQToManufacturerMutation.process(
      asignRFQResponse => asignRFQResponse,
      asignRFQResponse => {
        if (asignRFQResponse.state === 'ASSIGNED') {
          Alert.success(
            `Succes! RFQ assigned to ${this.assignedManufacturerTarget.assignee.initiative.title}`,
          );
          this.LoadRfqQuery();
        } else {
          Alert.error('Error assigning the rfq');
        }
      },
      { subPath: 'assignRequestForQuote' },
    );
  };

  @action AssignRFQToManufacturer = () => {
    this.assignRFQToManufacturerMutation.mutate();
  };

  @computed get rfqAssignedManufacturers() {
    const { sessions } = this.rfqApp;
    const assignees = [];

    if (sessions === null) {
      return [];
    }

    for (let i = 0; i < sessions.length; i += 1) {
      const a = sessions.apps[i].supplier.initiative;
      const index = assignees.findIndex(x => x.id === a.id);

      if (index === -1) {
        assignees.push(a);
      }
    }
    return assignees;
  }

  @action uploadFiles = (
    files,
    uploadCallback,
    cancelledCallback,
    options = { immadiate: false },
  ) => {
    const { immediate } = options;

    files.forEach(file => {
      const fileManager = new FileAppManager(null, {});
      if (immediate) {
        clientStore.uploader.FastTrackFile(
          fileManager,
          file,
          uploadCallback,
          cancelledCallback,
        );
      } else {
        clientStore.uploader.QueueFile(
          fileManager,
          file,
          uploadCallback,
          cancelledCallback,
        );
      }
    });
  };

  @action AddLocalSupportingFiles = files => {
    this.rfqApp.areFileRequirementsValidatedByServer = false;
    this.rfqApp.jobSpec.build.supportingFilesBeingAdded = [...files];
    const uploadCallback = uploadedFile => {
      if (!this.rfqApp.jobSpec.build.supportingFiles)
        this.rfqApp.jobSpec.build.ResetSupportingFiles();
      this.rfqApp.jobSpec.build.supportingFiles.push(uploadedFile);
      this.rfqApp.jobSpec.build.RemoveSupportingFileBeingAdded(uploadedFile);
    };

    const cancelledCallback = cancelledFile => {
      this.rfqApp.jobSpec.build.RemoveSupportingFileBeingAdded(cancelledFile);
    };

    this.uploadFiles(files, uploadCallback, cancelledCallback);
  };

  @action AddSupportingFiles = files => {
    this.rfqApp.jobSpec.build.supportingFilesBeingAdded = [...files];
    const uploadedSupportingFileIds = [];

    const updateRFQ = () => {
      if (uploadedSupportingFileIds.length === 0) return;
      const updateInput = new Input({
        input: {
          requestForQuoteId: this.rfqId,
          jobSpec: {
            build: {
              supportingFiles: {
                toAdd: uploadedSupportingFileIds,
              },
            },
          },
        },
      });

      const onAddingSupportingFilesFinished = () => {
        this.rfqApp.jobSpec.build.supportingFilesBeingAdded = [];
      };

      const onAddingSupportingFilesSuccess = updateRequestForQuote => {
        // We need to manually update these because otherwise there's a bit of time where the files being added
        // disappear (this mutation where they are uploaded finished) but the system has not yet finished querying
        // the whole state.
        const updatedSupportingFiles =
          updateRequestForQuote.jobSpec.build.supportingFiles;
        this.rfqApp.jobSpec.build.SetSupportingFiles(updatedSupportingFiles);

        this.rfqApp.fileRequirements = updateRequestForQuote.fileRequirements;

        onAddingSupportingFilesFinished();
      };

      const onError = () => {
        Alert.error(
          'Apologies, there was an error uploading your supporting file - please try again',
        );
        onAddingSupportingFilesFinished();
      };

      const rfqMutation = new UpdateRFQMutation(updateInput, null, {}, true);
      rfqMutation.setCallbacks({
        onSysErr: onError,
        onUserErr: onError,
        onSuccess: onAddingSupportingFilesSuccess,
      });
      rfqMutation.mutate();
    };

    let uploadsFinished = 0;
    const uploadCallback = uploadedFile => {
      uploadsFinished += 1;
      uploadedSupportingFileIds.push(uploadedFile.id);
      if (uploadsFinished === files.length) updateRFQ();
    };
    const cancelledCallback = cancelledFile => {
      uploadsFinished += 1;
      this.rfqApp.jobSpec.build.RemoveSupportingFileBeingAdded(cancelledFile);
      if (uploadsFinished === files.length) updateRFQ();
    };

    this.uploadFiles(files, uploadCallback, cancelledCallback);
  };

  @action RemoveLocalSupportingFile = supportingFileId => {
    const { supportingFiles } = this.rfqApp.jobSpec.build;
    if (!supportingFiles) return;
    this.rfqApp.areFileRequirementsValidatedByServer = false;
    this.rfqApp.jobSpec.build.SetSupportingFiles(
      supportingFiles.filter(file => file.id !== supportingFileId),
    );
  };

  @action RemoveSupportingFile = (supportingFileId, callback) => {
    const updateInput = new Input({
      input: {
        requestForQuoteId: this.rfqId,
        jobSpec: {
          build: {
            supportingFiles: {
              toDelete: [supportingFileId],
            },
          },
        },
      },
    });

    const onError = () => {
      Alert.error(
        'Apologies, there was an error removing your supporting file - please try again',
      );
      callback?.(false);
    };

    const rfqMutation = new UpdateRFQMutation(updateInput, null, {}, true);
    rfqMutation.setCallbacks({
      onSysErr: onError,
      onUserErr: onError,
      onSuccess: () => {
        callback?.(true);
      },
    });
    rfqMutation.mutate();
  };

  @action UpdatePrimaryFile = localFile => {
    const updateRFQ = uploadedFile => {
      const updateInput = new Input({
        input: {
          requestForQuoteId: this.rfqId,
          jobSpec: {
            build: {
              fileId: uploadedFile.id,
            },
          },
        },
      });

      const currentRevisionFileId =
        this.rfqApp?.intermediatorSession?.shareFile?.revision?.file?.id;

      const disposer = when(
        () =>
          this.rfqApp?.intermediatorSession?.shareFile?.revision?.file?.id !==
          currentRevisionFileId,
        () => {
          // - this previously called LoadCadFile, but then we don't know if the
          // conversion was successful, and LoadCadFile won't subscribe and wait
          // for the gltf conversion
          // - so instead I changed it so it would just reload the RFQ, which is
          // a bit ugly atm but should wait and load the new gltf correctly
          this.LoadRfqQuery();
        },
      );
      this.disposers.push(disposer);

      const onUpdatePrimaryFileFinished = () => {};

      const onUpdatePrimaryFileSuccess = () => {
        // Calling this.LoadRfqQuery(); is unnecessary because Apollo cache will
        // launch this query on its own, because the mutation response will partially
        // update the cache results for the original loadRFQQuery and to prevent
        // inconsistencies Apollo will requery.
        // this.LoadRfqQuery();

        onUpdatePrimaryFileFinished();
      };

      const onError = () => {
        disposer();
        Alert.error(
          'Apologies, there was an error uploading your file - please try again',
        );
        onUpdatePrimaryFileFinished();
      };

      const rfqMutation = new UpdateRFQMutation(updateInput, null, {}, true);
      rfqMutation.setCallbacks({
        onSysErr: onError,
        onUserErr: onError,
        onSuccess: onUpdatePrimaryFileSuccess,
      });
      rfqMutation.mutate();
    };
    this.uploadFiles([localFile], updateRFQ);
  };

  @action UpdateShippingAddress = address => {
    if (!address || !address.id) return;
    const updateInput = new Input({
      input: {
        requestForQuoteId: this.rfqId,
        addressId: address.id,
      },
    });

    const onUpdateShippingAddressFinished = () => {};

    const onUpdateShippingAddressSuccess = () => {
      this.rfqApp.address = address;
      onUpdateShippingAddressFinished();
    };

    const onError = () => {
      Alert.error('Apologies, there was an error updating your address');
      onUpdateShippingAddressFinished();
    };

    const rfqMutation = new UpdateRFQMutation(updateInput, null, {}, true);
    rfqMutation.setCallbacks({
      onSysErr: onError,
      onUserErr: onError,
      onSuccess: onUpdateShippingAddressSuccess,
    });
    rfqMutation.mutate();
  };

  @action QueryFileRequirements = input => {
    const q = new FileRequirementsQuery(this, {
      queryKey: 'fileRequirements',
      variables: {
        build: input,
      },
      onSysErr: () => {},
    });
    q.process(fileRequirements => {
      if (fileRequirements) {
        this.rfqApp.fileRequirements = fileRequirements;
        this.rfqApp.areFileRequirementsValidatedByServer = true;
      }
    });
    q.query();
  };

  @action BeginChangeAddress = (description = '') => {
    // the value of this.addressRefs is setup dynamically on
    // rendering the addresses field component
    if (this.addressRefs?.address) {
      this.addressRefs.address.manager.BeginChangeAddress(description);
    }
  };

  saveRFQFormToStorage = () => {
    try {
      const shareFileId = routerStore.route.params.sharedFileId;
      if (!shareFileId) return;
      const js = this.rfqApp.jobSpec.toJs();
      const rfqFormStorageKey = `rfq-form-state-${shareFileId}`;
      toSessionStorage(rfqFormStorageKey, js, true);
    } catch (error) {
      analytics.logError(error);
      analytics.sendEvent(
        'Failed RFQForm serialization in saveRFQFormToStorage',
        {
          error,
        },
        { ga: true },
      );
    }
  };

  @action LoadRFQFormFromStorage = () => {
    try {
      const shareFileId = routerStore.route.params.sharedFileId;
      if (!shareFileId) return;
      const rfqFormStorageKey = `rfq-form-state-${shareFileId}`;
      const js = fromSessionStorage(rfqFormStorageKey);
      if (!js) return;
      js.id = `fromStorageJobSpecId-${shareFileId}`;
      js.build.id = `fromStorageBuildId-${shareFileId}`;

      const valueTargetModels = {};
      Object.keys(js.build.valuesTarget).forEach(key => {
        if (!js.build.valuesTarget[key]) return;
        valueTargetModels[key] = OptionValueModel.Load(
          js.build.valuesTarget[key],
        );
      });

      const jobSpecModel = JobSpecModel.Load(js);

      this.AppRef('loadingJobSpec', JobSpecApp);
      this.loadingJobSpec = jobSpecModel;

      this.rfqApp.jobSpec.build.process = this.loadingJobSpec.build.process;
      this.rfqApp.jobSpec.build.material = this.loadingJobSpec.build.material;
      this.rfqApp.jobSpec.quantity = this.loadingJobSpec.quantity;
      this.rfqApp.jobSpec.build.valuesTarget = valueTargetModels;
    } catch (error) {
      analytics.logError(error);
      analytics.sendEvent(
        'Failed RFQForm deserialization in LoadRFQFormFromStorage',
        {
          error,
        },
        { ga: true },
      );
    }
  };
}
